<template>
  <main class="flex-1 flex flex-col bg-[#f3f5f4] relative overflow-hidden">
    <div class="flex-1 overflow-y-auto custom-scrollbar">

      <!-- Dark band: title, period, the four numbers -->
      <section class="relative bg-[#143535] text-white overflow-hidden">
        <!-- Living background: slow drifting light and a faint geometric weave -->
        <div class="pointer-events-none absolute inset-0" aria-hidden="true">
          <div class="dash-glow dash-glow-a"></div>
          <div class="dash-glow dash-glow-b"></div>
        </div>

        <div class="relative max-w-[2100px] mx-auto px-6 lg:px-10 pt-6 pb-10 space-y-8">
          <div class="flex justify-end">
            <RouterLink to="/profile" aria-label="Moj profil" class="flex items-center gap-3 rounded-xl px-2 py-1 text-[14px] transition-colors hover:bg-white/10">
              <div class="text-right leading-tight">
                <p class="font-semibold text-white/90">{{ auth.getName }}</p>
                <p class="text-white/45">{{ userRole }}</p>
              </div>
              <UserAvatar class="h-9 w-9 shrink-0 rounded-full text-[14px]" />
            </RouterLink>
          </div>

          <div class="flex flex-wrap items-start justify-between gap-6">
            <div>
              <h1 class="text-[34px] font-semibold tracking-tight leading-none">Deenly</h1>
              <p class="text-[15px] text-white/50 mt-2">{{ hijriSubtitle }} · {{ rangeCaption }}</p>
            </div>

            <div class="flex flex-wrap items-center gap-3">
              <div class="flex items-center gap-1 bg-white/[0.07] border border-white/10 rounded-xl p-1">
                <button
                  v-for="opt in presets"
                  :key="opt.key"
                  type="button"
                  class="px-3.5 py-2 text-[15px] font-semibold rounded-lg transition-colors"
                  :class="preset === opt.key ? 'bg-[#ecd79f] text-[#143535]' : 'text-white/70 hover:text-white hover:bg-white/5'"
                  @click="applyPreset(opt.key)"
                >
                  {{ opt.label }}
                </button>
              </div>
              <label class="flex items-center gap-2 bg-white/[0.07] border border-white/10 rounded-xl px-3 py-2 text-[15px]" :class="preset === 'day' ? 'ring-2 ring-[#ecd79f]' : ''">
                <span class="text-white/50">Day</span>
                <input v-model="singleDay" type="date" :max="today" class="bg-transparent outline-none font-semibold text-white [color-scheme:dark]" @change="applyDay" />
              </label>
              <label class="flex items-center gap-2 bg-white/[0.07] border border-white/10 rounded-xl px-3 py-2 text-[15px]" :class="preset === 'custom' ? 'ring-2 ring-[#ecd79f]' : ''">
                <span class="text-white/50">From</span>
                <input v-model="customFrom" type="date" :max="customTo || today" class="bg-transparent outline-none font-semibold text-white [color-scheme:dark]" @change="applyCustom" />
                <span class="text-white/50">to</span>
                <input v-model="customTo" type="date" :min="customFrom" :max="today" class="bg-transparent outline-none font-semibold text-white [color-scheme:dark]" @change="applyCustom" />
              </label>
            </div>
          </div>

          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard dark :value="formatCompact(overview?.installs?.value)" label="New users" :badge="deltaBadge(overview?.installs)" :badge-class="deltaClassDark(overview?.installs)" :spark="spark(users, 'new_users')" caption="New devices that signed in" />
            <StatCard dark :value="formatCompact(overview?.active_users?.value)" label="Active users" :badge="deltaBadge(overview?.active_users)" :badge-class="deltaClassDark(overview?.active_users)" :spark="spark(users, 'active_users')" spark-color="#8fd3c8" caption="People who used the app" />
            <StatCard dark :value="formatCompact(overview?.paywall_views?.value)" label="Paywall views" :badge="deltaBadge(overview?.paywall_views)" :badge-class="deltaClassDark(overview?.paywall_views)" :spark="spark(events, 'paywall_view')" spark-color="#e9b86a" caption="Times the paywall was shown" />
            <StatCard dark :value="formatCompact(overview?.purchases?.value)" label="Purchases" :badge="conversionBadge" badge-class="bg-[#ecd79f] text-[#143535]" :spark="spark(events, 'purchase_success')" spark-color="#ecd79f" :caption="`${formatNumber(overview?.active_subscribers)} active subscribers right now`" />
          </div>
        </div>
      </section>

      <!-- Tabs -->
      <div class="relative z-10 max-w-[2100px] mx-auto px-6 lg:px-10 -mt-5">
        <div class="inline-flex items-center gap-1 bg-white border border-[#1c4848]/10 rounded-xl p-1 shadow-sm">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            class="px-4 py-2 text-[15px] font-semibold rounded-lg transition-colors"
            :class="tab === t.key ? 'bg-[#1c4848] text-white' : 'text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5'"
            @click="tab = t.key"
          >
            {{ t.label }}
          </button>
        </div>
      </div>

      <div class="max-w-[2100px] mx-auto px-6 lg:px-10 py-6 space-y-6">
        <p v-if="analyticsStore.error" class="text-base text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          {{ analyticsStore.error }}
        </p>

        <!-- Overview -->
        <template v-if="tab === 'overview'">
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <MetricChartCard v-model="chartView" :views="chartViews" class="xl:col-span-2" />
            <ConversionFunnelCard title="From install to payment" subtitle="How many people reach each step" :steps="analyticsStore.funnel" />
          </div>
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <RankedListCard title="Most used features" subtitle="Opens in this period" :rows="featureRows" :label-for="featureLabel" unit="opens" :limit="6" />
            <RankedListCard title="Locked features people tried" subtitle="Tapped without a subscription" :rows="analyticsStore.locked" :label-for="featureLabel" unit="hits" color="#c9a227" :limit="6" empty-text="Nobody hit a locked feature in this period." />
            <RankedListCard title="What people buy" subtitle="Completed purchases by plan" :rows="analyticsStore.products" :label-for="productLabel" unit="purchases" color="#ecd79f" empty-text="No purchases in this period yet." />
          </div>
        </template>

        <!-- Users -->
        <template v-else-if="tab === 'users'">
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <MetricChartCard v-model="usersView" :views="chartViews.filter((v) => ['new', 'people', 'opens'].includes(v.key))" class="xl:col-span-2" />
            <div class="space-y-6">
              <ConversionFunnelCard title="From install to payment" subtitle="How many people reach each step" :steps="analyticsStore.funnel" />
              <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm">
                <h3 class="text-[22px] font-semibold tracking-tight text-[#1c4848]">Device accounts</h3>
                <p class="text-[15px] text-[#1c4848]/55 mt-1">What the server saw in this period</p>
                <dl class="mt-5 space-y-3 text-[15px]">
                  <div class="flex justify-between gap-4"><dt class="text-[#1c4848]/60">Accounts created</dt><dd class="font-semibold text-[#1c4848] tabular-nums">{{ formatNumber(overview?.accounts_created?.value) }}</dd></div>
                  <div class="flex justify-between gap-4"><dt class="text-[#1c4848]/60">…new users</dt><dd class="font-semibold text-[#1c4848] tabular-nums">{{ formatNumber(overview?.installs?.value) }}</dd></div>
                  <div class="flex justify-between gap-4"><dt class="text-[#1c4848]/60">…our test devices</dt><dd class="font-semibold text-[#1c4848] tabular-nums">{{ formatNumber(testAccounts) }}</dd></div>
                  <div class="flex justify-between gap-4 pt-3 border-t border-[#1c4848]/10"><dt class="text-[#1c4848]/60">Accounts, all time</dt><dd class="font-semibold text-[#1c4848] tabular-nums">{{ formatNumber(overview?.total_users) }}</dd></div>
                </dl>
                <p class="text-[13px] text-[#1c4848]/45 mt-4 leading-relaxed">Every new device ID is a new user. The ID is kept in the iPhone Keychain, so a reinstall signs back into the same account and is not counted twice. Test devices (any sandbox purchase) are left out of every number.</p>
              </div>
            </div>
          </div>
        </template>

        <!-- Features -->
        <template v-else-if="tab === 'features'">
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <MetricChartCard v-model="featuresView" :views="chartViews.filter((v) => ['features', 'opens'].includes(v.key))" class="xl:col-span-3" />
            <RankedListCard title="Features" subtitle="Opens, and how many different people" :rows="featureRows" :label-for="featureLabel" unit="opens" :limit="20" empty-text="No feature opens recorded in this period." />
            <RankedListCard title="Locked features people tried" subtitle="Tapped without a subscription" :rows="analyticsStore.locked" :label-for="featureLabel" unit="hits" color="#c9a227" :limit="12" empty-text="Nobody hit a locked feature in this period." />
            <RankedListCard title="Tabs" subtitle="Which part of the app people go to" :rows="tabRows" :label-for="tabLabel" unit="visits" color="#2a6666" :limit="8" empty-text="No tab visits recorded." />
          </div>
        </template>

        <!-- Engagement: how long people stay, what they think, what brings them back -->
        <template v-else-if="tab === 'engagement'">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard :value="eng.sessions?.total ? duration(eng.sessions.avg_seconds) : '–'" label="Average visit" :badge="`${formatNumber(eng.sessions?.total)} visits`" caption="From opening the app to leaving it" />
            <StatCard :value="formatCompact(notificationOpens)" label="Opened from a notification" :badge="notificationShare" caption="Taps on any of our notifications" />
            <StatCard :value="helpfulRate('ask_deenly')" label="Ask Deenly: helpful" :badge="ratingCount('ask_deenly')" caption="Share of answers rated 👍" />
            <StatCard :value="helpfulRate('dream')" label="Dream AI: helpful" :badge="ratingCount('dream')" caption="Share of readings rated 👍" />
          </div>
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <BarListCard title="Where the time goes" subtitle="Total time on each screen, and the average visit" :rows="screenTimeRows" :limit="10" empty-text="No screen time yet. Starts with the next app update." note="Only time with the screen on top counts — not time in the background or under another screen." />
            <BarListCard title="How long a visit lasts" subtitle="Visits by length" :rows="sessionRows" color="#2a6666" empty-text="No visits recorded yet. Starts with the next app update." />
            <RankedListCard title="Notifications that bring people back" subtitle="Taps, by notification" :rows="eng.notifications || []" :label-for="notificationLabel" unit="taps" color="#c9a227" empty-text="No notification taps yet. Starts with the next app update." />
          </div>
          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <BarListCard title="AI answers people found helpful" subtitle="Thumbs up out of all ratings" :rows="feedbackRows" :max="100" color="#2a7a6e" empty-text="No answers rated yet. Starts with the next app update." />
            <BarListCard title="Why an answer did not help" subtitle="Reasons picked after a thumbs down" :rows="reasonRows" color="#e07a2c" empty-text="No thumbs-down reasons yet." />
            <RankedListCard title="Opened from a widget or a link" subtitle="Home-screen widgets and prayer:// links" :rows="eng.deep_links || []" :label-for="linkSourceLabel" unit="opens" color="#2a6666" empty-text="No widget or link opens yet. Starts with the next app update." />
          </div>
        </template>

        <!-- Paywall & money -->
        <template v-else-if="tab === 'money'">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard :value="formatCompact(subs.trials)" label="Free trials started" badge="App Store" :spark="spark(events, 'purchase_success')" spark-color="#1c4848" />
            <StatCard :value="formatCompact(subs.paid)" label="Paid subscriptions" badge="App Store" />
            <StatCard :value="formatCompact(subs.lifetime)" label="Lifetime purchases" badge="App Store" />
            <StatCard :value="formatCompact(overview?.active_subscribers)" label="Active subscribers now" badge="all time" />
          </div>
          <MetricChartCard v-model="moneyView" :views="chartViews.filter((v) => ['paywall'].includes(v.key))" />
          <PaywallFunnelCard :rows="analyticsStore.paywalls" :label-for="sourceLabel" />
          <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <PaywallSourcesCard title="Paywall conversion by entry point" subtitle="Share of views that ended in a purchase" :rows="paywallRows" />
            <RankedListCard title="What people buy" subtitle="Completed purchases by plan" :rows="analyticsStore.products" :label-for="productLabel" unit="purchases" color="#ecd79f" empty-text="No purchases in this period yet." />
          </div>
        </template>

        <!-- Log: the main feed first, everything else around it -->
        <template v-else>
          <div class="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl border border-[#1c4848]/10 shadow-sm px-5 py-3">
            <div class="flex flex-wrap items-center gap-3">
              <span class="text-[14px] font-medium text-[#1c4848]/60">Whose phones</span>
              <div class="inline-flex items-center gap-1 bg-[#1c4848]/5 rounded-xl p-1">
                <button
                  v-for="d in deviceOptions"
                  :key="d.key"
                  type="button"
                  class="px-3.5 py-1.5 text-[14px] font-semibold rounded-lg transition-colors"
                  :class="logDevices === d.key ? 'bg-[#1c4848] text-white shadow-sm' : 'text-[#1c4848]/60 hover:text-[#1c4848]'"
                  :title="d.hint"
                  @click="logDevices = d.key"
                >
                  {{ d.label }}
                </button>
              </div>
            </div>
            <span class="flex items-center gap-2 text-[13px] font-medium" :class="isLive ? 'text-emerald-700' : 'text-[#1c4848]/50'">
              <span v-if="isLive" class="relative flex w-2 h-2">
                <span class="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span class="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              {{ isLive ? 'Live — new activity appears every 15 seconds' : 'A past period — pick Today for live updates' }}
            </span>
          </div>

          <div class="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard v-for="t in logTiles" :key="t.key" :value="formatCompact(t.value)" :label="t.label" :caption="t.caption" />
          </div>

          <MetricChartCard v-model="logChart" :views="logChartViews" />

          <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <LiveActivityFeed
              title="Latest activity"
              :subtitle="`Everything in ${rangeCaption.split(' · ')[0]}, newest first — click a user to follow one person`"
              :items="analyticsStore.recent"
              :loading="analyticsStore.recentLoading"
              :has-more="analyticsStore.recentHasMore"
              :devices="logDevices"
              :picked-user="pickedUser"
              :live="isLive"
              wrapper-class="xl:col-span-2 h-[1200px]"
              @filters="(f) => analyticsStore.loadRecent(f)"
              @more="analyticsStore.loadRecent(analyticsStore.recentFilters, true)"
            />
            <div class="flex flex-col gap-6 h-[1200px] min-h-0">
              <TopUsersCard class="flex-1" title="Busiest people" subtitle="Most activity in this period — click to follow one" :rows="analyticsStore.logTopUsers" @pick="pickUser" />
              <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar rounded-2xl">
                <EventBreakdownTable title="Everything being logged" subtitle="Every event type in this period" :rows="analyticsStore.logBreakdown" :limit="10" />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <LiveActivityFeed
              v-for="panel in logPanels"
              :key="panel.key"
              compact
              :live="isLive"
              :title="panel.title"
              :subtitle="panel.subtitle"
              :items="analyticsStore.logPanels[panel.key] || []"
              :empty-text="panel.empty"
              wrapper-class="h-[600px]"
            />
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import StatCard from './widgets/StatCard.vue';
import MetricChartCard from './widgets/MetricChartCard.vue';
import ConversionFunnelCard from './widgets/ConversionFunnelCard.vue';
import EventBreakdownTable from './widgets/EventBreakdownTable.vue';
import PaywallSourcesCard from './widgets/PaywallSourcesCard.vue';
import PaywallFunnelCard from './widgets/PaywallFunnelCard.vue';
import LiveActivityFeed from './widgets/LiveActivityFeed.vue';
import RankedListCard from './widgets/RankedListCard.vue';
import BarListCard from './widgets/BarListCard.vue';
import TopUsersCard from './widgets/TopUsersCard.vue';
import { featureLabel, productLabel, screenLabel, sourceLabel } from './featureLabels.js';
import { useAnalyticsStore } from '@/store/analytics/index.js';
import { useAuthStore } from '@/store/auth/index.js';
import UserAvatar from '@/components/app/dashboard/UserAvatar.vue';

const hijriSubtitle = computed(() => {
  try {
    return new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
  } catch {
    return '';
  }
});

// --- Tabs ----------------------------------------------------------------------

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'features', label: 'Features' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'money', label: 'Paywall & money' },
  { key: 'log', label: 'Log' },
];
const tab = ref('overview');

// --- Range: any window of whole days (UTC); presets, one exact day, or from–to -------

const analyticsStore = useAnalyticsStore();

const auth = useAuthStore();
const userRole = computed(() => {
  const first = auth.user?.data?.roles?.[0];
  return first ? first.replace(/[-_]/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Administrator';
});
const iso = (date) => date.toISOString().slice(0, 10);
const today = iso(new Date());
const daysAgo = (n) => { const d = new Date(); d.setUTCDate(d.getUTCDate() - n); return iso(d); };
const monthStart = (offset = 0) => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + offset); return iso(d); };
const monthEnd = (offset = 0) => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + offset + 1); d.setUTCDate(0); return iso(d); };

const presets = [
  { key: 'today', label: 'Today', from: () => today, to: () => today },
  { key: 'yesterday', label: 'Yesterday', from: () => daysAgo(1), to: () => daysAgo(1) },
  { key: '7', label: '7 days', from: () => daysAgo(6), to: () => today },
  { key: '30', label: '30 days', from: () => daysAgo(29), to: () => today },
  { key: '90', label: '90 days', from: () => daysAgo(89), to: () => today },
  { key: 'month', label: 'This month', from: () => monthStart(0), to: () => today },
  { key: 'lastmonth', label: 'Last month', from: () => monthStart(-1), to: () => monthEnd(-1) },
  { key: 'year', label: 'This year', from: () => `${new Date().getUTCFullYear()}-01-01`, to: () => today },
];

const preset = ref('30');
const range = ref({ from: daysAgo(29), to: today });
const customFrom = ref(range.value.from);
const customTo = ref(range.value.to);
const singleDay = ref(today);

function setRange(from, to, key) {
  preset.value = key;
  range.value = { from, to };
  customFrom.value = from;
  customTo.value = to;
}
function applyPreset(key) {
  const p = presets.find((x) => x.key === key);
  if (p) setRange(p.from(), p.to(), key);
}
function applyDay() {
  if (singleDay.value) setRange(singleDay.value, singleDay.value, 'day');
}
function applyCustom() {
  if (!customFrom.value || !customTo.value) return;
  const [from, to] = customFrom.value <= customTo.value ? [customFrom.value, customTo.value] : [customTo.value, customFrom.value];
  setRange(from, to, 'custom');
}

const rangeLength = computed(() => Math.round((Date.parse(range.value.to) - Date.parse(range.value.from)) / 86_400_000) + 1);
const interval = computed(() => (rangeLength.value > 120 ? 'month' : 'day'));
const rangeCaption = computed(() => {
  const fmt = (d) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${d}T00:00:00Z`));
  const span = range.value.from === range.value.to ? fmt(range.value.from) : `${fmt(range.value.from)} – ${fmt(range.value.to)}`;
  return `${span} · vs. the ${rangeLength.value} days before`;
});
const overview = computed(() => analyticsStore.overview);
const users = computed(() => analyticsStore.users);
const events = computed(() => analyticsStore.timeSeries);

function loadAnalytics() {
  analyticsStore.loadAll({ start_date: range.value.from, end_date: range.value.to, interval: interval.value });
}
watch(range, loadAnalytics, { deep: true });
onMounted(loadAnalytics);

// --- Log tab -------------------------------------------------------------------

const deviceOptions = [
  { key: 'real', label: 'Real devices', hint: 'App Store users only — no TestFlight, Xcode, simulator or sandbox purchasers' },
  { key: 'all', label: 'All devices', hint: 'App Store users and our own test phones' },
  { key: 'test', label: 'Test devices', hint: 'Our TestFlight, Xcode and simulator builds, and sandbox purchasers' },
];
const logDevices = ref('real');
const isLive = computed(() => range.value.to === today);
const pickedUser = ref(null);

function pickUser(id) {
  // Reset first so picking the same person twice still refocuses the feed.
  pickedUser.value = null;
  nextTick(() => { pickedUser.value = id; });
}

function loadLog() {
  if (tab.value === 'log') analyticsStore.loadLog(logDevices.value);
}
// loadAll sets the period the log reads, so wait for it on a range change.
watch(range, () => setTimeout(loadLog, 0), { deep: true });
watch([tab, logDevices], loadLog);

let liveTimer = null;
let liveTicks = 0;
function liveTick() {
  if (tab.value !== 'log' || !isLive.value || document.hidden) return;
  analyticsStore.pollRecent();
  // The side panels and the chart move slower; refresh them every minute.
  if (++liveTicks % 4 === 0) loadLog();
}
onMounted(() => { liveTimer = setInterval(liveTick, 15_000); });
onBeforeUnmount(() => clearInterval(liveTimer));

const GROUPS = [
  { key: 'people', name: 'People (opens, onboarding)', color: '#1c4848' },
  { key: 'features', name: 'Features & reading', color: '#2a7a6e' },
  { key: 'engagement', name: 'Engagement (time, ratings, taps)', color: '#8fd3c8' },
  { key: 'money', name: 'Paywall & purchases', color: '#c9a227' },
  { key: 'server', name: 'Server (sign-ins, prayer times)', color: '#b8c4c4' },
];
const logChart = ref('activity');
const logChartViews = computed(() => {
  const t = analyticsStore.logTimeline || { labels: [], series: [] };
  const hourly = t.interval === 'hour';
  // Buckets are UTC; hours are shown in the viewer's own time, like the feed.
  const labels = (t.labels || []).map((label) => {
    if (hourly) return new Date(`${label.replace(' ', 'T')}:00Z`).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (t.interval === 'month') return label;
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(`${label}T00:00:00Z`));
  });
  const byKey = Object.fromEntries((t.series || []).map((s) => [s.key, s.data]));
  const series = GROUPS.map((g) => ({ key: g.key, name: g.name, color: g.color, data: byKey[g.key] || [] }));
  const total = { key: 'total', name: 'All rows', color: '#ecd79f', bars: true, data: labels.map((_, i) => series.reduce((sum, s) => sum + (Number(s.data[i]) || 0), 0)) };
  const appOnly = series.filter((s) => s.key !== 'server');
  return [
    { key: 'activity', icon: 'trend', color: '#1c4848', title: hourly ? 'Activity, hour by hour' : 'Activity over time', unit: 'rows', labels, series: appOnly,
      description: 'What people did in the app, by kind.', hint: 'Server rows (sign-ins, prayer-time lookups) are on the second view.' },
    { key: 'all', icon: 'bars', color: '#c9a227', title: 'Everything logged', unit: 'rows', labels, series: [total, ...series],
      description: 'Every row, including the server’s own.' },
  ];
});
const logTiles = computed(() => {
  const sums = Object.fromEntries((analyticsStore.logTimeline?.series || []).map((s) => [s.key, s.data.reduce((a, b) => a + b, 0)]));
  const all = Object.values(sums).reduce((a, b) => a + b, 0);
  return [
    { key: 'all', label: 'Rows logged', value: all, caption: 'Everything, server included' },
    { key: 'people', label: 'App opens & first steps', value: sums.people || 0, caption: 'Opens, onboarding, permissions' },
    { key: 'features', label: 'Feature & reading', value: sums.features || 0, caption: 'Tools, AI, books, audio' },
    { key: 'engagement', label: 'Engagement', value: sums.engagement || 0, caption: 'Time, ratings, notification taps' },
    { key: 'money', label: 'Paywall & purchases', value: sums.money || 0, caption: 'Every step to paying' },
  ];
});
const logPanels = [
  { key: 'money', title: 'Money', subtitle: 'Paywalls, purchases and locked features', empty: 'No paywall or purchase activity in this period.' },
  { key: 'ai', title: 'AI answers', subtitle: 'Dream readings and every 👍 / 👎', empty: 'No AI answers or ratings in this period.' },
  { key: 'firstSteps', title: 'First steps', subtitle: 'Onboarding and permission answers', empty: 'Nobody went through onboarding in this period.' },
  { key: 'comeback', title: 'What brings people back', subtitle: 'Notification taps, widget opens, rating prompts', empty: 'No notification or widget opens in this period.' },
  { key: 'reading', title: 'Reading & listening', subtitle: 'Books, pages, bookmarks, audio', empty: 'No reading in this period.' },
  { key: 'time', title: 'Time spent', subtitle: 'Visit length and time on each screen', empty: 'No time tracked in this period.' },
];

// --- Charts -------------------------------------------------------------------

const chartView = ref('new');
const usersView = ref('new');
const featuresView = ref('features');
const moneyView = ref('paywall');

function seriesOf(source, key, name, color, extra = {}) {
  const found = (source?.series ?? []).find((line) => line.key === key);
  return { key, name: name || found?.name || key, data: found?.data ?? [], color, ...extra };
}
const spark = (source, key) => seriesOf(source?.value ?? source, key).data;

const chartViews = computed(() => {
  const u = users.value;
  const e = events.value;
  const newPeople = seriesOf(u, 'new_users', 'New users that day', '#ecd79f', { bars: true, tileName: 'New users in this period' });
  let running = 0;
  const cumulative = { key: 'new_cumulative', name: 'Total so far', color: '#1c4848', agg: 'last', axis: 'right', data: newPeople.data.map((n) => (running += Number(n) || 0)) };
  return [
    { key: 'new', icon: 'bars', color: '#1c4848', title: 'New users', unit: 'people', labels: u.labels, series: [newPeople, cumulative],
      description: 'New people per day, with the running total.', hint: 'Every new device that signed in. A reinstall keeps its ID and is not counted again; our test devices are left out.' },
    { key: 'people', icon: 'people', color: '#2a7a6e', title: 'People using the app', unit: 'people', labels: u.labels,
      series: [seriesOf(u, 'active_users', 'People using the app', '#1c4848'), seriesOf(u, 'new_users', 'Of which new', '#ecd79f', { bars: true })],
      description: 'Different people per day, and how many were new.' },
    { key: 'opens', icon: 'trend', color: '#c9a227', title: 'App opens', unit: 'opens', labels: e.labels, series: [seriesOf(e, 'app_open', 'App opens', '#1c4848', { bars: true })],
      description: 'How often the app was opened each day.', hint: 'One person can open it many times. This is habit, not reach.' },
    { key: 'features', icon: 'grid', color: '#2a6666', title: 'Feature opens', unit: 'opens', labels: e.labels, series: [seriesOf(e, 'feature_open', 'Feature opens', '#2a6666', { bars: true })],
      description: 'Feature opens per day.', hint: 'Hifz AI, Ask Deenly, khutbah, dreams, tools, tabs.' },
    { key: 'paywall', icon: 'card', color: '#c9a227', title: 'Paywall, step by step, per day', unit: 'total', labels: e.labels,
      series: [
        seriesOf(e, 'paywall_view', 'Seen', '#c9a227'), seriesOf(e, 'paywall_dismiss', 'Closed', '#9ca3af'),
        seriesOf(e, 'purchase_start', 'Tapped a plan', '#2a6666'), seriesOf(e, 'purchase_cancel', 'Cancelled', '#e07a2c'),
        seriesOf(e, 'purchase_success', 'Paid', '#1c4848', { bars: true }),
      ],
      description: 'Paywall seen, closed, plan tapped, cancelled, paid.', hint: 'Per day. A plan tapped means the App Store sheet opened.' },
  ];
});

// --- Derived rows ----------------------------------------------------------------

const TAB_KEYS = new Set(['home_feed', 'library', 'kids', 'mosque_finder', 'islamic_tools', 'home', 'quran', 'book', 'map', 'mosques', 'tools', 'more']);
const featureRows = computed(() => (analyticsStore.features ?? []).filter((row) => !TAB_KEYS.has(row.value)));
const tabRows = computed(() => (analyticsStore.features ?? []).filter((row) => TAB_KEYS.has(row.value)));
const tabLabel = (value) => featureLabel(value).replace('Tab: ', '');
const paywallRows = computed(() => (analyticsStore.paywalls ?? []).map((row) => ({ ...row, source: sourceLabel(row.source) })));
const testAccounts = computed(() => Math.max(0, Number(overview.value?.accounts_created?.value ?? 0) - Number(overview.value?.installs?.value ?? 0)));
const subs = computed(() => overview.value?.subscriptions ?? { trials: 0, paid: 0, lifetime: 0 });

// --- Engagement ----------------------------------------------------------------

const eng = computed(() => analyticsStore.engagement ?? {});
const screenTimeRows = computed(() => (eng.value.features ?? []).map((row) => ({
  key: row.value,
  label: screenLabel(row.value),
  value: row.total_seconds,
  display: duration(row.total_seconds),
  hint: `avg ${duration(row.avg_seconds)} · ${formatNumber(row.users)} ${row.users === 1 ? 'user' : 'users'}`,
})));
const sessionRows = computed(() => {
  const buckets = eng.value.sessions?.buckets ?? [];
  const total = buckets.reduce((sum, b) => sum + b.total, 0);
  return total ? buckets.map((b) => ({ key: b.key, label: b.label, value: b.total, display: formatNumber(b.total), hint: `${Math.round((b.total / total) * 100)}%` })) : [];
});
const feedbackRows = computed(() => (eng.value.feedback ?? []).map((row) => ({
  key: row.value,
  label: featureLabel(row.value),
  value: row.up_rate,
  display: `${row.up_rate}%`,
  hint: `${formatNumber(row.up)} 👍 · ${formatNumber(row.down)} 👎`,
})));
const REASONS = { inaccurate: 'Inaccurate', unclear: 'Unclear', too_long: 'Too long', off_topic: 'Did not answer the question' };
const reasonRows = computed(() => (eng.value.feedback_reasons ?? []).map((row) => ({
  key: `${row.feature}:${row.value}`,
  label: `${REASONS[row.value] || row.value} · ${featureLabel(row.feature)}`,
  value: row.total,
  display: formatNumber(row.total),
})));
const NOTIFICATIONS = { prayer: 'Prayer time (adhan)', quran_reminder: 'Quran reading reminder', feed: 'New content', hifz: 'Hifz review reminder', trial: 'Trial ending reminder', other: 'Other' };
const notificationLabel = (value) => NOTIFICATIONS[value] || featureLabel(value);
const linkSourceLabel = (value) => ({ widget: 'Home-screen widget', link: 'Link' }[value] || featureLabel(value));
const notificationOpens = computed(() => (eng.value.notifications ?? []).reduce((sum, row) => sum + row.total, 0));
const notificationShare = computed(() => {
  const opens = Number(eng.value.app_opens ?? 0);
  return opens ? `${Math.round((notificationOpens.value / opens) * 100)}% of app opens` : 'no app opens yet';
});
function feedbackFor(feature) {
  return (eng.value.feedback ?? []).find((row) => row.value === feature);
}
function helpfulRate(feature) {
  const row = feedbackFor(feature);
  return row && row.up + row.down ? `${row.up_rate}%` : '–';
}
function ratingCount(feature) {
  const row = feedbackFor(feature);
  const n = row ? row.up + row.down : 0;
  return `${formatNumber(n)} ${n === 1 ? 'rating' : 'ratings'}`;
}
/** 75 → "1 min 15 s", 4000 → "1 h 6 min". */
function duration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

// --- Formatting ------------------------------------------------------------------

function formatCompact(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '0';
  if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
  if (number >= 10_000) return `${(number / 1_000).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US').format(number);
}
function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value ?? 0));
}
function deltaBadge(metric) {
  const change = metric?.change_percent;
  if (change === null || change === undefined) return 'no earlier data';
  return `${change > 0 ? '+' : ''}${change}% vs before`;
}
function deltaClassDark(metric) {
  const change = metric?.change_percent;
  if (change === null || change === undefined || change === 0) return 'bg-white/10 text-white/70';
  return change > 0 ? 'bg-emerald-400/20 text-emerald-200' : 'bg-rose-400/20 text-rose-200';
}
const conversionBadge = computed(() => {
  const rate = overview.value?.paywall_conversion_rate;
  return rate === undefined || rate === null ? '—' : `${rate}% of paywall views`;
});
</script>

<style scoped>
/* Two soft lights drifting slowly over the solid dark band. Nothing else moves. */
.dash-glow { position: absolute; border-radius: 9999px; will-change: transform; }
.dash-glow-a { width: 900px; height: 900px; left: -300px; top: -500px;
  background: radial-gradient(circle, rgba(236,215,159,0.16) 0%, rgba(236,215,159,0.05) 40%, rgba(236,215,159,0) 70%);
  animation: glow-a 30s ease-in-out infinite; }
.dash-glow-b { width: 1000px; height: 1000px; right: -350px; top: -600px;
  background: radial-gradient(circle, rgba(120,214,196,0.13) 0%, rgba(120,214,196,0.04) 40%, rgba(120,214,196,0) 70%);
  animation: glow-b 38s ease-in-out infinite; }
@keyframes glow-a { 0%,100% { transform: translate(0,0); } 50% { transform: translate(380px,120px); } }
@keyframes glow-b { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-420px,140px); } }
@media (prefers-reduced-motion: reduce) { .dash-glow { animation: none; } }
</style>
